import { Injectable } from "@angular/core";
import {
  Actions,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { AccountJwtService } from "@youm-front/account-jwt/data-access";
import { AccountFacade } from "@youm-front/account/data-access";
import { Errors } from "@youm-front/account/domain";
import { YoumAPIFacade } from "@youm-front/core";
import { LokiActions } from "@youm-front/loki/data-access";
import { asyncScheduler, EMPTY, merge, Observable } from "rxjs";
import {
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  pairwise,
  scan,
  skip,
  switchMap,
  takeUntil,
  tap,
  throttleTime
} from "rxjs/operators";
import { ErrorData, SocketConnectError, SocketLifecycleEvent } from "youm-lib";
import { RoomActions, RoomV2Actions } from "../actions";
import { RoomContextFacade } from "../facades";

/**
 * Cause of a room socket closed because the account token is gone (sign out,
 * token deleted from the storage): never reconnected, the user must reload.
 */
export const ROOM_SOCKET_SIGNED_OUT = "signed-out";

const RESTORE_LOG_THROTTLE_MS = 60_000;

// Steps of a recovery logged after a reconnect, within this time
const RECOVERY_LOG_WINDOW_MS = 60_000;

@Injectable()
export class RoomYoumAPIEffects implements OnRunEffects {
  // Token of the current room socket, null when there is none
  private socketToken: null | string = null;
  // Last reconnect of the room socket, to time the recovery steps
  private reconnectedAt: null | number = null;

  constructor(
    private readonly actions$: Actions,
    private readonly accountFacade: AccountFacade,
    private readonly accountJwtService: AccountJwtService,
    private readonly roomContextFacade: RoomContextFacade,
    private readonly youmAPIFacade: YoumAPIFacade
  ) {}

  initYoumAPI$ = createEffect(
    () =>
      this.accountJwtService.accountJwt$.pipe(
        distinctUntilChanged(),
        tap((_) => this.youmApiConnect(_))
      ),
    { dispatch: false }
  );

  // Token changes seen by the room: gone (the socket is closed as signed out),
  // replaced (the socket follows the new one), restored after an accidental
  // loss (the socket stays). Never the token itself.
  logTokenChanges$ = createEffect(() =>
    merge(
      this.accountJwtService.accountJwt$.pipe(
        distinctUntilChanged(),
        pairwise(),
        map(([previous, current]) => this.tokenChangeLog(previous, current)),
        filter((_): _ is NonNullable<typeof _> => _ !== null)
      ),
      // A repeated loss is restored every 500 ms: one log line a minute, with
      // the number of restores since the room started
      this.accountJwtService.accountJwtEvents$.pipe(
        filter((_) => _.type === "restored"),
        scan((restoresTotal) => restoresTotal + 1, 0),
        throttleTime(RESTORE_LOG_THROTTLE_MS, asyncScheduler, {
          leading: true,
          trailing: true
        }),
        map((restoresTotal) =>
          this.log("room-socket.token.restored", { restoresTotal })
        )
      ),
      this.accountJwtService.accountJwtEvents$.pipe(
        filter((_) => _.type !== "restored"),
        map((_) =>
          this.log(`room-socket.token.${_.type}`, {
            reason: "reason" in _ ? _.reason : undefined
          })
        )
      )
    )
  );

  onSocketConnected$ = createEffect(
    () =>
      this.accountJwtService.accountJwt$.pipe(
        distinctUntilChanged(),
        switchMap((_) =>
          _
            ? this.youmAPIFacade.connected$.pipe(
                filter((_) => _),
                skip(1),
                tap(() => this.onSocketConnected())
              )
            : EMPTY
        )
      ),
    { dispatch: false }
  );

  onSocketConnectError$ = createEffect(
    () =>
      this.youmAPIFacade.connectError$.pipe(
        tap((_) => this.onSocketConnectError(_))
      ),
    { dispatch: false }
  );

  onSocketDisconnect$ = createEffect(
    () =>
      this.youmAPIFacade.disconnect$.pipe(
        tap((_) => this.onSocketDisconnect(_))
      ),
    { dispatch: false }
  );

  // Every disconnect (reason, expected or not), retry and result goes to the
  // logs: the browser console is not collected.
  logSocketLifecycle$ = createEffect(() =>
    this.youmAPIFacade.lifecycle$.pipe(
      tap((_) => {
        if (_.type === "reconnect") {
          this.reconnectedAt = Date.now();
        }
        // A first connect (new token): its join is not a recovery
        if (_.type === "connect") {
          this.reconnectedAt = null;
        }
      }),
      map((_) => this.log(`room-socket.${_.type}`, socketLogData(_)))
    )
  );

  // What the reconnect restored: room membership, own camera, room streams
  logRecovery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RoomActions.joinRoomSuccess,
        RoomActions.joinRoomFailure,
        RoomActions.publishWebcamStreamSuccess,
        RoomActions.publishWebcamStreamFailure,
        RoomActions.listRoomStreamsSuccess,
        RoomActions.listRoomStreamsFailure
      ),
      filter(
        () =>
          this.reconnectedAt !== null &&
          Date.now() - this.reconnectedAt <= RECOVERY_LOG_WINDOW_MS
      ),
      map((action) =>
        this.log("room-socket.recovery", {
          step: action.type,
          sinceReconnectMs: Date.now() - (this.reconnectedAt as number),
          ...recoveryStepData(action)
        })
      )
    )
  );

  onError$ = createEffect(
    () =>
      merge(
        this.youmAPIFacade.responseError$,
        this.youmAPIFacade.listenErrorEvent()
      ).pipe(tap((_) => this.onError(_))),
    { dispatch: false }
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(RoomActions.init, RoomV2Actions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(RoomActions.destroy, RoomV2Actions.destroy)
            )
          )
        )
      )
    );
  }

  private onSocketConnectError(error: SocketConnectError) {
    if (error.data) {
      return this.onError(error.data);
    }

    // TODO https://liveedev.atlassian.net/browse/YOUM-235
    // this.noticeFacade.noticeCreate(
    //   new NoticeCreate1Cmd({
    //     kind: "error",
    //     data: `Socket connect error: ${error.message} ${
    //       error.data ? JSON.stringify(error.data) : ""
    //     }`
    //   })
    // );
    console.error("Socket connect error", error);
  }

  private onSocketDisconnect(reason: string) {
    if (reason === "io client disconnect") {
      return;
    }

    // TODO https://liveedev.atlassian.net/browse/YOUM-235
    // return this.noticeFacade.noticeCreate(
    //   new NoticeCreate1Cmd({
    //     kind: "error",
    //     data: `Socket disconnect: ${reason}`
    //   })
    // );
    console.error(`Socket disconnect: ${reason}`);
  }

  private onSocketConnected() {
    // TODO https://liveedev.atlassian.net/browse/YOUM-235
    // return this.noticeFacade.noticeCreate(
    //   new NoticeCreate1Cmd({
    //     kind: "info",
    //     data: `Socket connected`
    //   })
    // );
    console.log("Socket connected");
  }

  private onError(error: ErrorData) {
    switch (error.code) {
      case Errors.Unauthorized: {
        return this.accountFacade.signOut();
      }
      default:
        // TODO https://liveedev.atlassian.net/browse/YOUM-235
        // return this.noticeFacade.noticeCreate(
        //   new NoticeCreate1Cmd({
        //     kind: "error",
        //     data: `Error: ${error.message} (${JSON.stringify({
        //       code: error.code,
        //       errorId: error.errorId
        //     })})`
        //   })
        // );
        console.error("Socket onError", error);
    }
  }

  /**
   * The room socket follows the account token: a new token replaces the socket,
   * no token closes it for good.
   */
  private youmApiConnect(token: null | string) {
    const hadSocket = this.socketToken !== null;
    this.socketToken = token;

    if (!token) {
      // Closed with a cause: the room must not look connected (no stream is
      // published or received anymore), the user is asked to reload.
      this.youmAPIFacade.disconnect(
        hadSocket ? ROOM_SOCKET_SIGNED_OUT : undefined
      );
      return;
    }

    this.youmAPIFacade.disconnect();
    this.youmAPIFacade.connect({
      token: `Bearer ${token}`
    });
  }

  // A token gone or replaced (not a first token)
  private tokenChangeLog(previous: null | string, current: null | string) {
    if (!previous) {
      return null;
    }
    if (!current) {
      return this.log("room-socket.token.cleared", {
        cause: ROOM_SOCKET_SIGNED_OUT
      });
    }
    return this.log("room-socket.token.replaced", {});
  }

  private log(event: string, data: object) {
    return LokiActions.log({
      args: [
        event,
        data,
        { roomContextId: this.roomContextFacade.roomContextId }
      ]
    });
  }
}

function socketLogData(event: SocketLifecycleEvent) {
  // The type is the log event name
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { type, ...data } = event;
  return data;
}

function recoveryStepData(action: Action): object {
  const error = (action as { error?: unknown }).error;
  if (error === undefined) {
    return {};
  }
  if (typeof error === "string") {
    return { error };
  }
  const { code, message } = (error ?? {}) as {
    code?: number;
    message?: string;
  };
  return { error: { code, message } };
}
